// src/pages/Notes.jsx
// The notes editor — sits between mapping review and Expert Review Dashboard
// AI drafts all 22 notes, accountant edits each one, adds custom notes, marks as approved
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FileEdit } from 'lucide-react'
import { generateAllNotes, generateNote, getFirm, getVerificationStatus, saveJobProgress } from '../api/kailux'
import BackButton from '../components/BackButton'
import CollapsibleSidebar from '../components/CollapsibleSidebar'
import { isUnmappedAccount, buildFiguresFromAccounts } from '../utils/accountMapping'

import Icon from '../components/Icon'
import { Dot } from '../components/Icon'

import { T, alpha } from '../theme/theme'
import { withEditedText } from '../utils/noteEdits'
const STATUS_CONFIG = {
  draft:    { label:'Draft',    color:T.yl, bg:T.yld },
  reviewed: { label:'Reviewed', color:T.bl, bg:T.bld },
  approved: { label:'Approved', color:T.gn, bg:T.gnd },
}

const DEFAULT_NOTE_TITLES = {
  "1":  "Basis of Preparation",
  "2":  "Property, Plant and Equipment",
  "3":  "Intangible Assets",
  "4":  "Inventories",
  "5":  "Trade and Other Receivables",
  "6":  "Cash and Cash Equivalents",
  "7":  "Stated Capital / Members' Contributions",
  "8":  "Income Tax",
  "9":  "Borrowings",
  "10": "Loans from Shareholders",
  "11": "Trade and Other Payables",
  "12": "Deferred Revenue",
  "13": "Revenue",
  "14": "Cost of Sales",
  "15": "Other Income",
  "16": "Employee Costs",
  "17": "Finance Costs",
  "18": "Related Party Transactions",
  "19": "Financial Instruments and Risk Management",
  "20": "Going Concern",
  "21": "Events after the Reporting Date",
  "22": "Standards Issued but Not Yet Effective",
}

// Strip AI markdown so note text renders as clean prose in the textarea and PDF
export const stripMarkdown = (text) => {
  if (!text) return ''
  return text
    .replace(/^#{1,6}\s+/gm, '')           // ## headings
    .replace(/\*\*(.+?)\*\*/gs, '$1')      // **bold**
    .replace(/\*(.+?)\*/gs, '$1')          // *italic*
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1') // `code`
    .replace(/^[-*]\s+/gm, '')             // - bullet points
    .replace(/^\d+\.\s+/gm, '')            // 1. numbered lists
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')    // [link](url)
    .replace(/^---+$/gm, '')               // horizontal rules
    .replace(/\n{3,}/g, '\n\n')            // excess blank lines
    .trim()
}


export default function Notes() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { firm }  = useAuth()

  const client        = location.state?.client
  const mappings      = location.state?.mappings      || {}
  const accounts      = location.state?.accounts      || []
  const prior_figures        = location.state?.prior_figures        || {}
  const first_year           = location.state?.first_year           || false
  const prior_not_available  = location.state?.prior_not_available  || false
  const financialYear  = location.state?.financial_year ||
                         sessionStorage.getItem('kailux:financial_year') ||
                         null
  const afsJobId      = location.state?.afsJobId      || null
  // FIX (Sep 2026 — round-trip opaque restore payload, see BankStatement.jsx/
  // BankStatementBalanceSheet.jsx/Review.jsx): just passed through here, not
  // read, so the bank-statement "Back to transactions" wipeout fix keeps
  // working now that Notes sits between Review and the balance-sheet screen.
  const bs_snapshot   = location.state?.bs_snapshot   || null
  // Phase 1 (Jul 2026): explicit period from the free date-range picker,
  // forwarded the same way afsJobId/financial_year already are.
  const yearEnd     = location.state?.year_end ||
                       sessionStorage.getItem('kailux:year_end') || ''
  const periodStart = location.state?.period_start ||
                       sessionStorage.getItem('kailux:period_start_display') || ''
  // FIX (kailux-fixes-log.md — "Back to Mapping loses all trial-balance
  // progress"): ISO-format period fields, distinct from the display-
  // formatted yearEnd/periodStart above — needed so "Back to Mapping"
  // below can hand TrialBalance.jsx's resume-hydration effect a value it
  // can actually parse. May legitimately be absent (a job resumed via AFS
  // History rather than walked start-to-finish this session won't have
  // them) — the "Back to Mapping" handler below tolerates that.
  const periodStartIso = location.state?.period_start_iso || null
  const periodEndIso   = location.state?.period_end_iso   || null
  // FIX (Sep 2026 — bank-statement flow now routes through Notes too, see
  // AdditionalDocuments.jsx/BankStatementBalanceSheet.jsx): forwarded to
  // Review below exactly as the trial-balance flow's own (absent) source
  // flag would be, and used by the Back button further down to return to
  // the balance-sheet screen instead of /trial-balance, which this flow
  // never visited.
  const source                = location.state?.source
  const bank_statement_source = location.state?.bank_statement_source || false

  const [notes,        setNotes]        = useState({})
  // BUG FIX: this used to default to false. The mount effect below decides
  // whether to auto-generate only after an async getFirm() call resolves, so
  // for that brief window React rendered the full notes-editor UI (with an
  // empty `notes` object) before flipping to the "Drafting..." loading
  // screen — a visible blank/incomplete-page flash right before notes were
  // drafted. Defaulting to true shows the loading screen immediately on
  // mount instead; the effect below now explicitly turns it off on the
  // auto_notes-OFF branch (previously it never did, since generateNotes()
  // — the only path that cleared it — wasn't called in that branch).
  const [generating,   setGenerating]   = useState(true)
  const [genProgress,  setGenProgress]  = useState('')
  const [activeNote,   setActiveNote]   = useState('1')
  const [editingText,  setEditingText]  = useState('')
  const [editingTitle, setEditingTitle] = useState('')
  const [autoNotes,    setAutoNotes]    = useState(true)
  const [regenLoading, setRegenLoading] = useState(null)
  const [addingCustom, setAddingCustom] = useState(false)
  const [customTitle,  setCustomTitle]  = useState('')
  const [verificationStatus, setVerificationStatus] = useState({}) // note_key → "source-verified" | "tb-derived"

  // Categories with a natural debit balance — accumulated as (debit − credit).
  // Shared with TrialBalance/Review/Statements and the backend's
  // aggregate_figures_from_accounts() — see utils/accountMapping.js.

  // Use figures already computed upstream (TrialBalance) if present — never silently
  // recompute and discard them. If absent (e.g. older flow), rebuild here via the
  // shared helper so unmapped accounts (including control/suspense rows) are
  // excluded exactly like everywhere else, rather than dropping them with no trace.
  const incomingFigures = location.state?.figures || null
  const unmappedAccounts = accounts.filter(acc => isUnmappedAccount(mappings[acc.code], acc.name))
  const figures = incomingFigures || buildFiguresFromAccounts(accounts, mappings)

  // Prefer user-selected year from TrialBalance; fall back to date-derived
  const deriveYear = () => {
    if (financialYear) return String(financialYear)
    const now = new Date()
    const monthNames = ['January','February','March','April','May','June',
      'July','August','September','October','November','December']
    const yearEndMonth = client?.year_end_month || 'February'
    const yearEndIdx   = monthNames.indexOf(yearEndMonth)
    if (yearEndIdx === -1) return String(now.getFullYear())
    return now.getMonth() >= yearEndIdx
      ? String(now.getFullYear())
      : String(now.getFullYear() - 1)
  }

  // Load firm setting and generate notes on mount.
  //
  // FIX (kailux-fixes-log.md — "Back to Notes regenerates unnecessarily"):
  // this used to call generateNotes() unconditionally whenever auto_notes
  // was on, completely ignoring notes already sitting in
  // location.state.notes. Review.jsx's "Back to Notes" button (handleBack)
  // has always forwarded the already-drafted, already-approved notes right
  // back here — but nothing ever read them, so every trip back from Expert
  // Review re-ran all ~22 Claude note-generation calls from scratch
  // (~30-50s of wasted AI spend), discarding any edits the preparer had
  // made. If notes were already passed in, restore them instead of
  // regenerating. The explicit per-note "Regenerate" action (regenLoading)
  // is untouched by this — a preparer can still force a fresh draft of any
  // individual note.
  useEffect(() => {
    if (!firm?.id) return
    const passedNotes = location.state?.notes
    if (passedNotes && Object.keys(passedNotes).length > 0) {
      setAutoNotes(true)
      setNotes(passedNotes)
      const firstRef = Object.keys(passedNotes)[0]
      if (firstRef) {
        setActiveNote(firstRef)
        setEditingText(passedNotes[firstRef].text || '')
        setEditingTitle(passedNotes[firstRef].title || '')
      }
      setGenerating(false)
      return
    }
    getFirm(firm.id)
      .then(({ data }) => {
        const shouldAutoNote = data.auto_notes !== false
        setAutoNotes(shouldAutoNote)
        if (shouldAutoNote) {
          generateNotes()
        } else {
          // auto_notes OFF — create blank placeholders
          const blanks = {}
          Object.entries(DEFAULT_NOTE_TITLES).forEach(([ref, title]) => {
            blanks[ref] = {
              ref, title, text: '', status: 'draft', custom: false,
            }
          })
          setNotes(blanks)
          setActiveNote('1')
          setEditingText('')
          setEditingTitle(DEFAULT_NOTE_TITLES['1'])
          setGenerating(false)
        }
      })
      .catch(() => {
        generateNotes()
      })
  }, [firm?.id])

  // Fetch source-verification status for badges (non-blocking — fails silently)
  useEffect(() => {
    if (!afsJobId) return
    getVerificationStatus(afsJobId)
      .then(({ verification_status }) => {
        if (verification_status) setVerificationStatus(verification_status)
      })
      .catch(() => {}) // non-fatal — badges simply won't show
  }, [afsJobId])

  const generateNotes = async () => {
    setGenerating(true)
    setGenProgress('Drafting all 22 notes with AI...')
    try {
      const { data } = await generateAllNotes({
        firm_id:         firm.id,
        client_id:       client?.id || '',
        company:         client?.name || '',
        // Phase 1 (Jul 2026): use the explicit picker date when available so the
        // AI-generated note text matches the real period, not the client's fixed
        // year_end_month default — otherwise a stub-period AFS would have the AI
        // path confidently naming the wrong month/year while the PDF cover page
        // (generate_afs.py) correctly says the real one.
        year_end:        yearEnd || `${client?.year_end_month || 'February'} ${deriveYear()}`,
        period_start:    periodStart || undefined,
        entity_type:     client?.entity_type || 'Pty Ltd',
        engagement_type: client?.engagement_type || firm?.engagement_type || 'compilation',
        figures,
        accounts,   // FIX: needed server-side to derive real Note 4 inventory category
        mappings,   // split and real Note 5 receivables split, instead of always naming
                    // categories / estimating a split that may not exist — see CLAUDE.md
                    // Known Issue #2
        first_year, prior_not_available, prior_figures,
                    // FIX: needed so Note 1's comparatives justification and Note 2's PPE
                    // roll-forward/disclosure logic can distinguish "genuinely first year"
                    // from "prior data simply wasn't supplied" in the AI-generated note text
      })
      const rawNotes = data.notes || {}
      const cleanNotes = {}
      Object.entries(rawNotes).forEach(([ref, n]) => {
        cleanNotes[ref] = { ...n, text: stripMarkdown(n.text || '') }
      })
      setNotes(cleanNotes)
      const firstKey = Object.keys(data.notes || {})[0] || '1'
      setActiveNote(firstKey)
      setEditingText(stripMarkdown(data.notes?.[firstKey]?.text || ''))
      setEditingTitle(data.notes?.[firstKey]?.title || '')
    } catch (err) {
      // Fallback — blank notes
      const blanks = {}
      Object.entries(DEFAULT_NOTE_TITLES).forEach(([ref, title]) => {
        blanks[ref] = { ref, title, text: '', status: 'draft', custom: false }
      })
      setNotes(blanks)
      setActiveNote('1')
      setEditingText('')
      setEditingTitle(DEFAULT_NOTE_TITLES['1'])
    } finally {
      setGenerating(false)
      setGenProgress('')
    }
  }

  // When user clicks a note in the sidebar — save current edits first
  const selectNote = (ref) => {
    if (activeNote) {
      setNotes(prev => ({
        ...prev,
        [activeNote]: {
          ...withEditedText(prev[activeNote], editingText),
          title: editingTitle,
        }
      }))
    }
    setActiveNote(ref)
    setEditingText(notes[ref]?.text || '')
    setEditingTitle(notes[ref]?.title || '')
  }

  const updateStatus = (ref, status) => {
    setNotes(prev => ({
      ...prev,
      [ref]: { ...withEditedText(prev[ref], editingText), title: editingTitle, status }
    }))
  }

  const regenSingleNote = async (ref) => {
    setRegenLoading(ref)
    try {
      const noteTitle = notes[ref]?.title || DEFAULT_NOTE_TITLES[ref] || `Note ${ref}`
      const { data } = await generateNote({
        firm_id:    firm.id,
        client_id:  client?.id || '',
        note_ref:   ref,
        note_title: noteTitle,
        context:    JSON.stringify(figures),
        company:    client?.name || '',
        year_end:   yearEnd || `${client?.year_end_month || 'February'} ${deriveYear()}`,
        period_start: periodStart || undefined,
      })
      const newText = stripMarkdown(data.note || '')
      setNotes(prev => ({
        ...prev,
        // A regenerated note is the AI's again (not the preparer's), and carries the figures fingerprint
        // the backend computed from the context it was written from, so staleness can be judged later.
        [ref]: { ...prev[ref], text: newText, status: 'draft', edited: false,
                 source: data.source || prev[ref]?.source,
                 figures_sig: data.figures_sig || undefined }
      }))
      if (activeNote === ref) setEditingText(newText)
    } catch (err) {
      console.error('Regen failed:', err)
    } finally {
      setRegenLoading(null)
    }
  }

  const addCustomNote = () => {
    if (!customTitle.trim()) return
    const customRef = `custom_${Date.now()}`
    const newNote = {
      ref: customRef, title: customTitle.trim(),
      text: '', status: 'draft', custom: true,
    }
    setNotes(prev => ({ ...prev, [customRef]: newNote }))
    setCustomTitle('')
    setAddingCustom(false)
    selectNote(customRef)
  }

  const deleteCustomNote = (ref) => {
    setNotes(prev => {
      const updated = { ...prev }
      delete updated[ref]
      return updated
    })
    if (activeNote === ref) {
      const remaining = Object.keys(notes).filter(r => r !== ref)
      if (remaining.length > 0) selectNote(remaining[0])
    }
  }

  // Save current edits and navigate to Review (Phase 2)
  const handleProceed = async () => {
    if (unmappedAccounts.length > 0) {
      window.alert(
        `${unmappedAccounts.length} account(s) have no confirmed IFRS category mapping ` +
        `(${unmappedAccounts.map(a => a.name).join(', ')}). They will not appear anywhere ` +
        `in the financial statements. Go back to Trial Balance and map them before proceeding.`
      )
      return
    }
    const finalNotes = {
      ...notes,
      ...(activeNote ? {
        [activeNote]: {
          ...withEditedText(notes[activeNote], editingText),
          title: editingTitle,
        }
      } : {})
    }

    const allNotesList = Object.values(finalNotes)
    const approved  = allNotesList.filter(n => n.status === 'approved').length
    const total     = allNotesList.length

    if (afsJobId) {
      try {
        await saveJobProgress(afsJobId, { stage: 'notes', notes_override: finalNotes })
      } catch (err) {
        console.error('Autosave failed (non-fatal):', err)
      }
    }

    navigate('/review', {
      state: {
        client, mappings, accounts,
        figures,           // BUG-NOTES-2 fix: pass computed figures so Review doesn't rebuild
        prior_figures, first_year, prior_not_available,
        financial_year: deriveYear(),
        year_end: yearEnd, period_start: periodStart,
        period_start_iso: periodStartIso, period_end_iso: periodEndIso,
        notes: finalNotes,
        notes_summary: { approved, total },
        afsJobId,
        source, bank_statement_source,
      }
    })
  }

  const noteKeys     = Object.keys(notes)
  const allNotesList = Object.values(notes)
  const approved     = allNotesList.filter(n => n.status === 'approved').length
  const reviewed     = allNotesList.filter(n => n.status === 'reviewed').length
  const drafts       = allNotesList.filter(n => n.status === 'draft').length
  const total        = allNotesList.length
  const activeNoteData = notes[activeNote]

  // ── Generating screen ─────────────────────────────────────────────────────
  if (generating) return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px' }}>
      <div style={{ maxWidth:'520px', width:'100%', textAlign:'center' }}>
        <div style={{ width:'72px', height:'72px', background:T.prg,
          borderRadius:'20px', margin:'0 auto 28px', display:'flex',
          alignItems:'center', justifyContent:'center' }}><FileEdit size={30} strokeWidth={1.5} color={T.onpri}/></div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'800', marginBottom:'8px' }}>
          Drafting Financial Statement Notes
        </div>
        <div style={{ fontSize:'14px', fontWeight:'400', color:T.lt, marginBottom:'32px', lineHeight:'1.7' }}>
          KaiLux is drafting all 22 mandatory IFRS for SMEs notes using your figures.
          You will review and approve each one before the PDF is generated.
        </div>
        <div style={{ display:'flex', justifyContent:'center', gap:'8px', marginBottom:'32px' }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width:'10px', height:'10px', borderRadius:'50%',
              background:T.or, animation:`bounce 1.2s ${i * 0.2}s infinite` }}/>
          ))}
        </div>
        <div style={{ background:T.s1, borderRadius:'8px', padding:'14px 20px',
          border:`1px solid ${T.bd}`, fontSize:'14px', color:T.mt }}>
          {genProgress || 'Generating notes...'}
        </div>
      </div>
      <style>{`
        @keyframes bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-10px)} }
      `}</style>
    </div>
  )

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex', flexDirection:'column' }}>

      {/* ── TOP BAR ── */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 24px', height:'62px', display:'flex', alignItems:'center',
        justifyContent:'space-between', position:'sticky', top:0, zIndex:50, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <BackButton onClick={() => {
              // FIX (Sep 2026 — bank-statement flow now routes through
              // Notes too): this flow never visits /trial-balance, so
              // sending it there on Back would land on a page expecting a
              // trial balance upload it never had — return to the
              // balance-sheet screen instead, with figures intact so it
              // can rehydrate every field (see BankStatementBalanceSheet's
              // `incomingFigures` handling). Matches the pattern already
              // used by Review.jsx's own Back button for this flow.
              if (bank_statement_source) {
                navigate('/bank-statement/balance-sheet', {
                  state: {
                    client, mappings, accounts, figures, prior_figures, first_year,
                    financialYear: deriveYear(), yearEndDate: yearEnd,
                    bs_snapshot,
                  }
                })
                return
              }
              navigate('/trial-balance', {
              // FIX (kailux-fixes-log.md — "Back to Mapping loses all
              // trial-balance progress"): this used to pass only {client},
              // dropping the already-mapped accounts/mappings on the floor
              // even though TrialBalance.jsx has had a working resume-
              // hydration effect for this exact shape since Phase 5 (Issue
              // A) — it just never received the data. That forced a
              // preparer correcting one account mapping after reaching
              // Notes/Expert Review to re-upload the trial balance and
              // re-run AI categorization from scratch, discarding any
              // manual corrections already made. Forwarding the full state
              // here means TrialBalance.jsx's own effect (setAccounts/
              // setMappings/setScreen('review')) does the rest — no changes
              // needed on that side.
              state: {
                client, mappings, accounts, figures,
                first_year, prior_not_available,
                financial_year: deriveYear(),
                afsJobId,
                period_start: periodStartIso, period_end: periodEndIso,
              },
            })}}
            label={bank_statement_source ? 'Back to Balance Sheet' : 'Back to Mapping'} textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          {client && (
            <span style={{ fontSize:'13px', color:T.mt,
              borderLeft:`1px solid ${T.bd}`, paddingLeft:'12px' }}>
              {client.name}
            </span>
          )}
          {client?.entity_type === 'Trust' && (
            <button type="button" onClick={() => navigate('/trust-details', { state: { ...location.state, financial_year: location.state?.financial_year || deriveYear(), returnTo: '/notes' } })}
              style={{ fontSize:'12px', padding:'4px 12px', borderRadius:'6px', border:`1px solid ${T.bd}`, background:'transparent', color:T.or, cursor:'pointer' }}>
              Trust details
            </button>
          )}
          <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
            background:T.ord, color:T.or, fontWeight:'700' }}>
            Notes Editor
          </span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          {/* Progress summary */}
          <div style={{ fontSize:'13px', color:T.mt, display:'flex', gap:'12px' }}>
            <span style={{ color:T.gn, fontWeight:'700' }}><Icon name="check" /> {approved} approved</span>
            <span style={{ color:T.bl }}>◈ {reviewed} reviewed</span>
            <span style={{ color:T.yl }}>◎ {drafts} draft</span>
          </div>
          <button onClick={handleProceed}
            style={{ padding:'8px 20px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px',
              fontWeight:'700', cursor:'pointer' }}>
            Expert Review →
          </button>
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div style={{ display:'flex', flex:1, overflow:'hidden', height:'calc(100vh - 62px)' }}>

        {/* ── SIDEBAR — note list ── */}
        <CollapsibleSidebar width="280px" bg={T.s1} borderColor={T.bd}
          accentFrom={T.or} accentTo={T.or2} mutedColor={T.mt}
          storageKey="kailux:notesSidebarCollapsed" ariaLabel="note list">
        <div style={{ background:T.s1,
          borderRight:`1px solid ${T.bd}`, display:'flex', flexDirection:'column',
          height:'100%' }}>

          {/* Progress bar */}
          <div style={{ padding:'16px', borderBottom:`1px solid ${T.bd}` }}>
            <div style={{ display:'flex', justifyContent:'space-between',
              fontSize:'12px', color:T.mt, marginBottom:'6px' }}>
              <span>Approval progress</span>
              <span style={{ color:T.or, fontWeight:'700' }}>{approved}/{total}</span>
            </div>
            <div style={{ background:T.bd, borderRadius:'999px', height:'5px', overflow:'hidden' }}>
              <div style={{ height:'100%', borderRadius:'999px',
                background:T.gn,
                width:`${total > 0 ? (approved/total)*100 : 0}%`,
                transition:'width 0.4s' }}/>
            </div>
          </div>

          {/* Note list */}
          <div style={{ flex:1, overflowY:'auto', padding:'8px' }}>
            {noteKeys.map(ref => {
              const n = notes[ref]
              const sc = STATUS_CONFIG[n?.status] || STATUS_CONFIG.draft
              const isActive = activeNote === ref
              // Verification badge — source-verified if this note has a source document
              const noteKey = `note_${ref}`
              const isVerified = verificationStatus[noteKey] === 'source-verified'
              return (
                <div key={ref} onClick={() => selectNote(ref)}
                  style={{ padding:'10px 12px', borderRadius:'8px', cursor:'pointer',
                    marginBottom:'4px', transition:'all 0.15s',
                    background: isActive ? T.ord : 'transparent',
                    border: `1px solid ${isActive ? T.or : 'transparent'}` }}
                  onMouseEnter={e => { if(!isActive) e.currentTarget.style.background = T.s2 }}
                  onMouseLeave={e => { if(!isActive) e.currentTarget.style.background = 'transparent' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'10px', color: isActive ? T.or : T.mt,
                        fontWeight:'700', marginBottom:'2px' }}>
                        {n?.custom ? 'CUSTOM' : `NOTE ${ref}`}
                        {isVerified && (
                          <span title="Source-verified" style={{ marginLeft:'5px', fontSize:'9px' }}>{<Dot color={T.gn} />}</span>
                        )}
                      </div>
                      <div style={{ fontSize:'13px', fontWeight:'600',
                        color: isActive ? T.tx : T.lt,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {n?.title || `Note ${ref}`}
                      </div>
                    </div>
                    <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 6px',
                      borderRadius:'6px', background:sc.bg, color:sc.color,
                      flexShrink:0, marginLeft:'6px', marginTop:'2px' }}>
                      {sc.label.toUpperCase()}
                    </span>
                  </div>
                </div>
              )
            })}

            {/* Add custom note */}
            {addingCustom ? (
              <div style={{ padding:'10px 12px', background:T.s2, borderRadius:'8px',
                border:`1px solid ${T.bd}`, marginTop:'8px' }}>
                <div style={{ fontSize:'10px', color:T.or, fontWeight:'700',
                  marginBottom:'8px' }}>NEW CUSTOM NOTE</div>
                <input value={customTitle} onChange={e => setCustomTitle(e.target.value)}
                  placeholder="Note title..."
                  autoFocus
                  style={{ width:'100%', padding:'8px 10px', background:T.s3,
                    border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx,
                    fontSize:'13px', fontFamily:"'Outfit',sans-serif",
                    outline:'none', boxSizing:'border-box', marginBottom:'8px' }}
                  onFocus={e => e.target.style.borderColor = T.or}
                  onBlur={e  => e.target.style.borderColor = T.bd}
                  onKeyDown={e => { if(e.key==='Enter') addCustomNote(); if(e.key==='Escape') setAddingCustom(false) }}/>
                <div style={{ display:'flex', gap:'4px' }}>
                  <button onClick={addCustomNote}
                    style={{ flex:1, padding:'4px', borderRadius:'6px', border:'none',
                      background:T.prg, color:T.onpri,
                      fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                      fontWeight:'700', cursor:'pointer' }}>Add</button>
                  <button onClick={() => setAddingCustom(false)}
                    style={{ padding:'6px 10px', borderRadius:'6px',
                      border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:'12px', cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingCustom(true)}
                style={{ width:'100%', padding:'8px', borderRadius:'8px', marginTop:'8px',
                  border:`1px dashed ${T.bd}`, background:'transparent', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer',
                  transition:'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=T.or; e.currentTarget.style.color=T.or }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.color=T.mt }}>
                + Add custom note
              </button>
            )}
          </div>
        </div>
        </CollapsibleSidebar>

        {/* ── EDITOR ── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' }}>
          {activeNoteData ? (
            <>
              {/* Editor header */}
              <div style={{ padding:'20px 28px', borderBottom:`1px solid ${T.bd}`,
                display:'flex', alignItems:'center', justifyContent:'space-between',
                flexShrink:0, background:T.bg }}>
                <div style={{ flex:1 }}>
                  <input value={editingTitle}
                    onChange={e => setEditingTitle(e.target.value)}
                    style={{ background:'transparent', border:'none', outline:'none',
                      fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800',
                      color:T.tx, width:'100%', marginBottom:'4px' }}/>
                  <div style={{ fontSize:'12px', color:T.mt }}>
                    {activeNoteData.custom ? 'Custom note' : `Mandatory IFRS for SMEs disclosure · Note ${activeNote}`}
                    {!autoNotes && ' · Manual entry mode'}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'8px', flexShrink:0 }}>
                  {/* Status buttons */}
                  {['draft','reviewed','approved'].map(status => {
                    const sc = STATUS_CONFIG[status]
                    const isActive = activeNoteData.status === status
                    return (
                      <button key={status} onClick={() => updateStatus(activeNote, status)}
                        style={{ padding:'7px 14px', borderRadius:'6px',
                          border:`1px solid ${isActive ? sc.color : T.bd}`,
                          background: isActive ? sc.bg : 'transparent',
                          color: isActive ? sc.color : T.mt,
                          fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                          fontWeight:'700', cursor:'pointer', transition:'all 0.2s' }}>
                        {sc.label}
                      </button>
                    )
                  })}
                  {/* Regen button */}
                  {autoNotes && (
                    <button onClick={() => regenSingleNote(activeNote)}
                      disabled={regenLoading === activeNote}
                      style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.mt,
                        fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                        fontWeight:'600', cursor: regenLoading === activeNote ? 'not-allowed' : 'pointer',
                        opacity: regenLoading === activeNote ? 0.6 : 1,
                        display:'flex', alignItems:'center', gap:'4px' }}>
                      {regenLoading === activeNote ? <Icon name="hourglass" /> : <Icon name="refresh" />} Regenerate
                    </button>
                  )}
                  {/* Delete custom */}
                  {activeNoteData.custom && (
                    <button onClick={() => deleteCustomNote(activeNote)}
                      style={{ padding:'7px 12px', borderRadius:'6px',
                        border:`1px solid ${alpha(T.rd, 0.3)}`,
                        background:alpha(T.rd, 0.08), color:T.rd,
                        fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                        cursor:'pointer' }}>
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Text editor */}
              <div style={{ flex:1, padding:'24px', overflow:'auto', background:T.bg }}>
                {!autoNotes && !editingText && (
                  <div style={{ background:alpha(T.bl, 0.08),
                    border:`1px solid ${alpha(T.bl, 0.2)}`, borderRadius:'8px',
                    padding:'14px 18px', marginBottom:'16px',
                    fontSize:'14px', color:T.bl, lineHeight:'1.6' }}>
                    <Icon name="pencil" /> Auto-notes is off — type your note text below.
                    This note will appear exactly as written in the final PDF.
                  </div>
                )}
                <textarea
                  value={editingText}
                  onChange={e => setEditingText(e.target.value)}
                  placeholder={autoNotes
                    ? 'AI-generated note text will appear here...'
                    : 'Type your note text here. This will appear in the AFS exactly as written...'}
                  style={{ width:'100%', minHeight:'320px', padding:'20px',
                    background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
                    color:T.tx, fontSize:'14px', fontFamily:"Georgia, 'Times New Roman', serif",
                    lineHeight:'1.9', outline:'none', resize:'vertical',
                    boxSizing:'border-box', transition:'border 0.2s' }}
                  onFocus={e => e.target.style.borderColor = T.or}
                  onBlur={e  => e.target.style.borderColor = T.bd}/>

                <div style={{ marginTop:'12px', fontSize:'12px', color:T.mt, lineHeight:'1.6' }}>
                  <Icon name="tip" /> <strong>Professional tip:</strong> This text appears verbatim in the final PDF.
                  Review for accuracy, check all rand amounts, and approve when satisfied.
                  The accountant takes professional responsibility for all notes under ISRS 4410 / ISRE 2400 / ISA.
                </div>

                {/* Quick nav */}
                <div style={{ marginTop:'24px', display:'flex', justifyContent:'space-between' }}>
                  {(() => {
                    const keys = noteKeys
                    const idx  = keys.indexOf(activeNote)
                    const prev = idx > 0 ? keys[idx-1] : null
                    const next = idx < keys.length-1 ? keys[idx+1] : null
                    return (
                      <>
                        {prev ? (
                          <button onClick={() => selectNote(prev)}
                            style={{ padding:'8px 16px', borderRadius:'8px',
                              border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                              fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer' }}>
                            ← Previous note
                          </button>
                        ) : <div/>}
                        {next && (
                          <button
                            onClick={() => { updateStatus(activeNote, 'reviewed'); selectNote(next) }}
                            style={{ padding:'8px 16px', borderRadius:'8px', border:'none',
                              background:T.prg, color:T.onpri,
                              fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                              fontWeight:'700', cursor:'pointer' }}>
                            Mark reviewed & next →
                          </button>
                        )}
                      </>
                    )
                  })()}
                </div>
              </div>
            </>
          ) : (
            <div style={{ flex:1, display:'flex', alignItems:'center',
              justifyContent:'center', color:T.mt, fontSize:'14px' }}>
              Select a note from the sidebar
            </div>
          )}
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div style={{ background:T.s1, borderTop:`1px solid ${T.bd}`,
        padding:'16px 28px', display:'flex', alignItems:'center',
        justifyContent:'space-between', flexShrink:0 }}>
        <div style={{ fontSize:'14px', color:T.mt }}>
          {approved === total
            ? <span style={{ color:T.gn, fontWeight:'700' }}><Icon name="check" /> All {total} notes approved — ready for expert review</span>
            : <span>{total - approved} notes still need approval</span>}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          {approved < total && (
            <button
              onClick={() => {
                const allApproved = {}
                Object.entries(notes).forEach(([ref, n]) => {
                  allApproved[ref] = { ...n, status: 'approved' }
                })
                if (activeNote) {
                  allApproved[activeNote] = {
                    ...withEditedText(allApproved[activeNote], editingText),
                    title: editingTitle,
                  }
                }
                setNotes(allApproved)
              }}
              style={{ padding:'10px 20px', borderRadius:'8px',
                border:`1px solid ${T.gn}`, background:T.gnd, color:T.gn,
                fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                fontWeight:'700', cursor:'pointer' }}>
              Approve all notes
            </button>
          )}
          <button onClick={handleProceed}
            style={{ padding:'10px 28px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px',
              fontWeight:'800', cursor:'pointer',
              boxShadow:`0 4px 20px ${alpha(T.pri, 0.35)}` }}>
            Expert Review →
          </button>
        </div>
      </div>
    </div>
  )
}
